import { Analytics } from '@trello/atlassian-analytics';
import { sendErrorEvent } from '@trello/error-reporting';
import type { WorkspaceSettingsVisibilityPopoverContentQuery } from './WorkspaceSettingsVisibilityPopoverContentQuery.generated';
import { useWorkspaceSettingsVisibilityPopoverContentQuery } from './WorkspaceSettingsVisibilityPopoverContentQuery.generated';
import type { WorkspaceSettingsVisibilityPopoverContentUpdateVisibilityMutationVariables } from './WorkspaceSettingsVisibilityPopoverContentUpdateVisibilityMutation.generated';
import { useWorkspaceSettingsVisibilityPopoverContentUpdateVisibilityMutation } from './WorkspaceSettingsVisibilityPopoverContentUpdateVisibilityMutation.generated';
import type { FunctionComponent } from 'react';
import React, { useCallback, useMemo, useEffect } from 'react';
import styles from './WorkspaceSettingsVisibilityPopoverContent.less';
import { PopoverMenu, PopoverMenuButton } from 'app/src/components/PopoverMenu';
import { forNamespace } from '@trello/i18n';
import { PrivateIcon } from '@trello/nachos/icons/private';
import { UnlockedIcon } from '@trello/nachos/icons/unlocked';
import { PublicIcon } from '@trello/nachos/icons/public';
import { CheckIcon } from '@trello/nachos/icons/check';
import { WorkspaceSettingsOrganizationTestIds } from '@trello/test-ids';
import { IconGreenColor, IconRedColor } from '@trello/nachos/icon';
import { token } from '@trello/theme';

const format = forNamespace('org perms');

type Domain = NonNullable<
  WorkspaceSettingsVisibilityPopoverContentQuery['organization']
>['domain'];
type PermissionLevels = WorkspaceSettingsVisibilityPopoverContentUpdateVisibilityMutationVariables['visibility'];

interface VisibilityOptions {
  visibility: PermissionLevels;
  Icon: JSX.Element;
}

interface WorkspaceSettingsVisibilityPopoverContentComponentProps {
  domain?: Domain;
  currentVisibility?: PermissionLevels;
  disabledOptions?: PermissionLevels[];
  onChange?: (newVisibility: PermissionLevels) => void;
  isDisabled?: boolean;
}

export const WorkspaceSettingsVisibilityPopoverContentComponent: FunctionComponent<WorkspaceSettingsVisibilityPopoverContentComponentProps> = ({
  domain,
  currentVisibility,
  disabledOptions = [],
  onChange,
  isDisabled,
}) => {
  const visibilities = useMemo<VisibilityOptions[]>(
    () => [
      {
        visibility: 'private',
        Icon: (
          <PrivateIcon color={token('color.icon.accent.red', IconRedColor)} />
        ),
      },
      {
        visibility: 'domain',
        Icon: <UnlockedIcon />,
      },
      {
        visibility: 'public',
        Icon: (
          <PublicIcon
            color={token('color.icon.accent.green', IconGreenColor)}
          />
        ),
      },
    ],
    [],
  );

  const onUpdateVisibility = useCallback(
    (visibility: PermissionLevels) => (
      event: React.MouseEvent<HTMLButtonElement>,
    ) => {
      event.stopPropagation();
      event.preventDefault();

      onChange && onChange(visibility);
    },
    [onChange],
  );

  return (
    <div
      className={styles.workspaceSettingsVisibilityPopoverContent}
      data-testid={
        WorkspaceSettingsOrganizationTestIds.WorkspaceSettingsVisibilityPopoverContent
      }
    >
      <PopoverMenu>
        {visibilities
          .filter(
            ({ visibility }) =>
              !disabledOptions.includes(visibility) &&
              (visibility === 'domain' ? Boolean(domain) : true),
          )
          .map(({ visibility, Icon }) => (
            <PopoverMenuButton
              key={visibility}
              onClick={onUpdateVisibility(visibility)}
              description={format(`${visibility}.text`, {
                enterpriseName: domain?.displayName,
              })}
              icon={Icon}
              disabled={isDisabled}
            >
              <span className={styles.name}>
                {format(`${visibility}.name`)}
              </span>
              {currentVisibility === visibility && <CheckIcon size="small" />}
            </PopoverMenuButton>
          ))}
      </PopoverMenu>
    </div>
  );
};

interface WorkspaceSettingsVisibilityPopoverContentProps
  extends WorkspaceSettingsVisibilityPopoverContentComponentProps {
  orgId: string;
}

export const WorkspaceSettingsVisibilityPopoverContent: FunctionComponent<WorkspaceSettingsVisibilityPopoverContentProps> = ({
  orgId,
  onChange,
  disabledOptions,
  ...props
}) => {
  const [
    updateOrganization,
    { loading },
  ] = useWorkspaceSettingsVisibilityPopoverContentUpdateVisibilityMutation();
  const { data } = useWorkspaceSettingsVisibilityPopoverContentQuery({
    variables: {
      orgId,
    },
    waitOn: ['MemberHeader'],
  });

  useEffect(() => {
    Analytics.sendScreenEvent({
      name: 'workspaceVisibilityInlineDialog',
      containers: {
        workspace: {
          id: orgId,
        },
      },
    });
  }, [orgId]);

  const onUpdateVisibility = useCallback(
    async (visibility: PermissionLevels) => {
      const traceId = Analytics.startTask({
        taskName: 'edit-organization/prefs/permissionLevel',
        source: 'teamSettingsScreen',
      });
      try {
        Analytics.sendClickedButtonEvent({
          buttonName: 'changeWorkspaceVisibilityButton',
          source: 'teamSettingsScreen',
          containers: {
            workspace: {
              id: orgId,
            },
          },
          attributes: {
            taskId: traceId,
            updatedVisibility: visibility,
          },
        });

        await updateOrganization({
          variables: {
            orgId,
            visibility,
            traceId,
          },
          update(cache) {
            cache.modify({
              id: cache.identify({ __typename: 'Organization', id: orgId }),
              fields: {
                prefs: (prefs) => ({ ...prefs, permissionLevel: visibility }),
              },
            });
          },
        });

        Analytics.sendTrackEvent({
          action: 'changed',
          actionSubject: 'teamVisibilityRestriction',
          containers: {
            organization: {
              id: orgId,
            },
          },
          source: 'teamSettingsScreen',
          attributes: {
            value: visibility,
            taskId: traceId,
          },
        });
        Analytics.taskSucceeded({
          taskName: 'edit-organization/prefs/permissionLevel',
          source: 'teamSettingsScreen',
          traceId,
        });

        onChange && onChange(visibility);
      } catch (error) {
        Analytics.taskFailed({
          taskName: 'edit-organization/prefs/permissionLevel',
          source: 'teamSettingsScreen',
          traceId,
          // @ts-expect-error
          error,
        });

        // @ts-expect-error
        sendErrorEvent(error);
      }
    },
    [orgId, updateOrganization, onChange],
  );

  const domain = data?.organization?.domain;

  return (
    <WorkspaceSettingsVisibilityPopoverContentComponent
      {...props}
      domain={domain}
      disabledOptions={disabledOptions}
      onChange={onUpdateVisibility}
      isDisabled={loading}
    />
  );
};
